// Precondition: None
// Postcondition: Custom element for inline filer search with local + remote results
// Invariants: Curated results appear first, deduplicated by CIK, parent owns selection state

import { icon } from "../../../shared/icons.js";
import { searchFilers } from "../../lib/financeapis.js";
import { createLogger } from "../../lib/logger.js";
import investorsData from "../../../lib/superinvestors.json";

const log = createLogger("ft-filer-search");

interface Investor {
  label: string;
  name: string;
  cik: string;
  people?: string[];
  category?: string;
  region?: string;
}

interface SearchResult {
  cik: string;
  name: string;
  curated: boolean;
  category?: string;
  people?: string[];
  tickers?: string;
}

const investors = investorsData as Investor[];

if (!customElements.get("ft-filer-search")) {
  class FtFilerSearch extends HTMLElement {
    private input: HTMLInputElement | null = null;
    private dropdown: HTMLElement | null = null;
    private debounceTimer: number | undefined;
    private selectedIndex = -1;
    private abortController = new AbortController();
    private _selected: Set<string> = new Set();
    private currentResults: SearchResult[] = [];

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    set selected(ciks: Set<string>) {
      this._selected = ciks;
      this.updateCheckmarks();
    }

    get selected(): Set<string> {
      return this._selected;
    }

    connectedCallback(): void {
      log.debug("connected");
      this.render();
      this.setupReferences();
      this.setupEventListeners();
    }

    disconnectedCallback(): void {
      log.debug("disconnected");
      this.abortController.abort();
      clearTimeout(this.debounceTimer);
    }

    // Precondition: Shadow DOM attached
    // Postcondition: HTML structure rendered into shadow DOM
    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    private getTemplate(): string {
      return `
      <div class="filer-search">
        <span class="search-icon" aria-hidden="true">${icon("search", 16)}</span>
        <input
          type="search"
          placeholder="Search by name, ticker, or CIK..."
          autocomplete="off"
          role="combobox"
          aria-label="Search filers"
          aria-autocomplete="list"
          aria-controls="filer-results"
          aria-expanded="false"
          aria-haspopup="listbox"
        />
        <button class="clear-btn" type="button" aria-label="Clear search">${icon("x", 14)}</button>
        <ul id="filer-results" class="dropdown" role="listbox" hidden></ul>
      </div>
    `;
    }

    private getStyles(): string {
      return `
      :host {
        display: block;
      }

      .filer-search {
        position: relative;
      }

      .search-icon {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        color: var(--text-dim, #555);
        pointer-events: none;
        display: flex;
        align-items: center;
        z-index: 1;
      }

      input {
        width: 100%;
        min-height: 44px;
        padding: 0.85rem 2.5rem 0.85rem 2.75rem;
        font-family: var(--font-body, "Space Grotesk", system-ui, sans-serif);
        font-size: 0.9rem;
        color: var(--text-primary, #f5f5f5);
        background: var(--bg-card, #141414);
        border: 1px solid var(--bg-card-border, #1f1f1f);
        border-radius: 8px;
        outline: none;
        transition: border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-sizing: border-box;
      }

      input::placeholder {
        color: var(--text-dim, #555);
      }

      input:focus {
        border-color: rgba(201, 168, 76, 0.3);
        box-shadow: 0 0 0 3px rgba(201, 168, 76, 0.15);
      }

      input::-webkit-search-cancel-button {
        display: none;
      }

      .clear-btn {
        display: none;
        position: absolute;
        right: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        padding: 0;
        background: rgba(255, 255, 255, 0.08);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: var(--text-dim, #555);
        transition: color 0.15s ease, background 0.15s ease;
      }

      .clear-btn:hover {
        color: var(--text-primary, #f5f5f5);
        background: rgba(255, 255, 255, 0.15);
      }

      input:not(:placeholder-shown) ~ .clear-btn {
        display: flex;
      }

      .dropdown {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        background: var(--bg-card, #141414);
        border: 1px solid var(--bg-card-border, #1f1f1f);
        border-radius: 8px;
        max-height: 280px;
        overflow-y: auto;
        z-index: 100;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .dropdown[hidden] {
        display: none;
      }

      .dropdown li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.7rem 1rem;
        min-height: 44px;
        font-size: 0.85rem;
        color: var(--text-muted, #888);
        cursor: pointer;
        border-bottom: 1px solid rgba(255, 255, 255, 0.03);
        transition: background 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-sizing: border-box;
      }

      .dropdown li:last-child {
        border-bottom: none;
      }

      .dropdown li:hover,
      .dropdown li[aria-selected="true"] {
        background: rgba(201, 168, 76, 0.06);
        color: var(--text-primary, #f5f5f5);
      }

      .result-info {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        min-width: 0;
      }

      .result-name {
        font-weight: 500;
        color: var(--text-primary, #f5f5f5);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .result-meta {
        font-size: 0.75rem;
        color: var(--text-dim, #555);
      }

      .result-check {
        color: var(--accent, #c9a84c);
        font-size: 0.75rem;
        flex-shrink: 0;
        margin-left: 0.5rem;
        visibility: hidden;
      }

      .result-check.is-selected {
        visibility: visible;
      }

      .result-curated {
        display: inline-block;
        width: 6px;
        height: 6px;
        background: var(--accent, #c9a84c);
        border-radius: 50%;
        flex-shrink: 0;
        margin-right: 0.5rem;
      }

      .dropdown-status {
        padding: 1.25rem 1rem;
        font-size: 0.85rem;
        color: var(--text-dim, #555);
        text-align: center;
      }
    `;
    }

    // Precondition: Shadow DOM rendered
    // Postcondition: Element references stored
    private setupReferences(): void {
      this.input = this.shadowRoot?.querySelector("input") as HTMLInputElement;
      this.dropdown = this.shadowRoot?.querySelector(
        "#filer-results",
      ) as HTMLElement;
    }

    // Precondition: References set up
    // Postcondition: Event listeners attached with AbortController
    private setupEventListeners(): void {
      this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.input?.addEventListener(
        "input",
        () => {
          clearTimeout(this.debounceTimer);
          const query = this.input?.value.trim() || "";
          if (query.length < 2) {
            this.hideDropdown();
            return;
          }
          this.debounceTimer = window.setTimeout(() => this.search(query), 200);
        },
        { signal },
      );

      this.input?.addEventListener("keydown", (e) => this.handleKeydown(e), {
        signal,
      });

      this.input?.addEventListener(
        "focus",
        () => {
          const query = this.input?.value.trim() || "";
          if (query.length >= 2 && this.currentResults.length > 0) {
            this.showDropdown();
          }
        },
        { signal },
      );

      const clearBtn = this.shadowRoot?.querySelector(
        ".clear-btn",
      ) as HTMLButtonElement;
      clearBtn?.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          if (this.input) {
            this.input.value = "";
            this.input.focus();
          }
          this.currentResults = [];
          this.hideDropdown();
        },
        { signal },
      );

      this.dropdown?.addEventListener(
        "click",
        (e) => {
          const item = (e.target as HTMLElement).closest(
            "[data-cik]",
          ) as HTMLElement | null;
          if (item?.dataset.cik) {
            this.handleSelect(item.dataset.cik);
          }
        },
        { signal },
      );

      // Close dropdown on outside click
      document.addEventListener(
        "click",
        (e) => {
          if (
            !this.contains(e.target as Node) &&
            !this.shadowRoot?.contains(e.target as Node)
          ) {
            this.hideDropdown();
          }
        },
        { signal },
      );
    }

    // Precondition: query is at least 2 chars
    // Postcondition: Dropdown shows curated results immediately, then merges API results
    private async search(query: string): Promise<void> {
      const q = query.toLowerCase();
      log.log("search", { query });

      // 1. Instant local filter
      const curated = this.filterLocal(q);
      this.currentResults = curated;
      this.renderResults(curated, true);

      // 2. Parallel API call
      const result = await searchFilers(query);

      if (!result.ok) {
        log.error("search failed", { query, error: result.error });
        // Keep showing curated-only results
        if (curated.length === 0) {
          this.renderNoResults();
        }
        return;
      }

      const apiFilers = result.data.filers || [];
      log.debug("search results", {
        curated: curated.length,
        api: apiFilers.length,
      });

      // 3. Merge: curated first, then API results deduplicated by CIK
      const merged = this.mergeResults(curated, apiFilers);
      this.currentResults = merged;
      this.renderResults(merged, false);
    }

    // Precondition: q is lowercase query string
    // Postcondition: Returns curated investors matching label, people, or CIK prefix
    private filterLocal(q: string): SearchResult[] {
      return investors
        .filter(
          (inv) =>
            inv.label.toLowerCase().includes(q) ||
            inv.name.toLowerCase().includes(q) ||
            (inv.people || []).some((p) => p.toLowerCase().includes(q)) ||
            inv.cik.startsWith(q),
        )
        .slice(0, 8)
        .map((inv) => ({
          cik: inv.cik,
          name: inv.label,
          curated: true,
          category: inv.category,
          people: inv.people,
        }));
    }

    // Precondition: curated is local results, apiFilers is API response
    // Postcondition: Returns merged list with curated first, deduplicated by CIK
    private mergeResults(
      curated: SearchResult[],
      apiFilers: Array<{ cik: string; entity: string; tickers?: string }>,
    ): SearchResult[] {
      const seen = new Set(curated.map((r) => r.cik));
      const apiResults: SearchResult[] = [];

      for (const filer of apiFilers) {
        if (seen.has(filer.cik)) continue;
        seen.add(filer.cik);

        // Cross-reference with superinvestors
        const match = investors.find((inv) => inv.cik === filer.cik);
        if (match) {
          apiResults.push({
            cik: match.cik,
            name: match.label,
            curated: true,
            category: match.category,
            people: match.people,
            tickers: filer.tickers,
          });
        } else {
          const name = filer.entity.split("(")[0].trim();
          apiResults.push({
            cik: filer.cik,
            name,
            curated: false,
            tickers: filer.tickers,
          });
        }
      }

      return [...curated, ...apiResults].slice(0, 12);
    }

    // Precondition: results is array of search results
    // Postcondition: Dropdown rendered with results, visible
    private renderResults(results: SearchResult[], loading: boolean): void {
      if (!this.dropdown) return;

      if (results.length === 0 && !loading) {
        this.renderNoResults();
        return;
      }

      const items = results
        .map((r, i) => {
          const isSelected = this._selected.has(r.cik);
          const meta = this.buildMetaText(r);
          const curatedDot = r.curated
            ? `<span class="result-curated" title="Curated"></span>`
            : "";
          const checkClass = isSelected
            ? "result-check is-selected"
            : "result-check";
          const selected =
            i === 0 ? ' aria-selected="true"' : ' aria-selected="false"';
          return `<li role="option" id="filer-opt-${i}" data-cik="${r.cik}"${selected}>
          ${curatedDot}<div class="result-info">
            <span class="result-name">${this.escapeHtml(r.name)}</span>
            ${meta ? `<span class="result-meta">${this.escapeHtml(meta)}</span>` : ""}
          </div>
          <span class="${checkClass}" aria-hidden="true">\u2713</span>
        </li>`;
        })
        .join("");

      const loadingHtml = loading
        ? `<li class="dropdown-status" aria-live="polite">Searching...</li>`
        : "";

      this.dropdown.innerHTML = items + loadingHtml;
      this.selectedIndex = 0;
      this.showDropdown();
      this.input?.setAttribute("aria-activedescendant", "filer-opt-0");
    }

    private renderNoResults(): void {
      if (!this.dropdown) return;
      this.dropdown.innerHTML = `<li class="dropdown-status">No filers found</li>`;
      this.selectedIndex = -1;
      this.showDropdown();
    }

    private buildMetaText(r: SearchResult): string {
      const parts: string[] = [];
      if (r.people?.length) parts.push(r.people.join(", "));
      if (r.tickers) parts.push(r.tickers);
      if (r.category) parts.push(r.category);
      return parts.join(" \u00B7 ");
    }

    // Precondition: cik is a CIK string
    // Postcondition: Emits filer-select or filer-deselect event
    private handleSelect(cik: string): void {
      const result = this.currentResults.find((r) => r.cik === cik);
      if (!result) return;

      if (this._selected.has(cik)) {
        log.debug("deselect", { cik });
        this.dispatchEvent(
          new CustomEvent("filer-deselect", {
            bubbles: true,
            composed: true,
            detail: { cik },
          }),
        );
      } else {
        log.debug("select", { cik, name: result.name });
        this.dispatchEvent(
          new CustomEvent("filer-select", {
            bubbles: true,
            composed: true,
            detail: {
              cik,
              name: result.name,
              curated: result.curated,
              category: result.category,
            },
          }),
        );
      }

      this.updateCheckmarks();
    }

    // Precondition: Event is KeyboardEvent from input
    // Postcondition: Handles arrow keys, Enter, Escape
    private handleKeydown(e: KeyboardEvent): void {
      const items = this.dropdown?.querySelectorAll('[role="option"]');
      if (!items?.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateSelection(items);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(items);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          const item = items[this.selectedIndex] as HTMLElement;
          if (item.dataset.cik) {
            this.handleSelect(item.dataset.cik);
          }
        }
      } else if (e.key === "Escape") {
        this.hideDropdown();
      }
    }

    // Precondition: items is NodeList of option elements
    // Postcondition: Updates visual selection and aria-activedescendant
    private updateSelection(items: NodeListOf<Element>): void {
      items.forEach((item, i) => {
        item.setAttribute(
          "aria-selected",
          i === this.selectedIndex ? "true" : "false",
        );
        if (i === this.selectedIndex) {
          (item as HTMLElement).scrollIntoView({ block: "nearest" });
          this.input?.setAttribute("aria-activedescendant", `filer-opt-${i}`);
        }
      });
      if (this.selectedIndex < 0) {
        this.input?.removeAttribute("aria-activedescendant");
      }
    }

    // Postcondition: Checkmarks updated to reflect current selected set
    private updateCheckmarks(): void {
      if (!this.dropdown) return;
      const items = this.dropdown.querySelectorAll("[data-cik]");
      items.forEach((item) => {
        const el = item as HTMLElement;
        const check = el.querySelector(".result-check");
        if (check) {
          const isSelected = this._selected.has(el.dataset.cik || "");
          check.classList.toggle("is-selected", isSelected);
        }
      });
    }

    private showDropdown(): void {
      if (!this.dropdown) return;
      this.dropdown.hidden = false;
      this.input?.setAttribute("aria-expanded", "true");
    }

    private hideDropdown(): void {
      if (!this.dropdown) return;
      this.dropdown.hidden = true;
      this.dropdown.innerHTML = "";
      this.selectedIndex = -1;
      this.input?.setAttribute("aria-expanded", "false");
      this.input?.removeAttribute("aria-activedescendant");
    }

    private escapeHtml(str: string): string {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define("ft-filer-search", FtFilerSearch);
}
